<template>
  <Container>
    <Card hasNoBorder>
      <HeadingMedium
        :class="$style.headerBorder"
      >
        {{ addAndroidDevice.byodTitle }}
        <Icon
          icon="error"
        />
      </HeadingMedium>
      <ParagraphBody>
        <span>
          Allow users to enroll personal mobile devices in JumpCloud's <em :class="$style.textLink">
            Android EMM</em> so that they can access company resources, such as email,
          calendar, contacts and documents. Select the field below and instruct
          the user to log into the User Portal and go to <strong>
            Security > Enroll Your Android Device</strong>. After the user scans the QR code
          to enroll the phone <em :class="$style.textLink">into Android EMM</em>,
          the enrolled Android device is visible in the Admin Portal.
        </span>
      </ParagraphBody>
      <template v-slot:footer>
        <GoogleEmmDynamicSelectModal v-if="showDynamicSelectModal" />
      </template>
    </Card>
    <QrCodeEnrollmentModal
      v-if="isShowEnrollmentInstructions"
      :enterpriseObjectId="enterpriseResponseBody.enterpriseObjectId"
      :headerText="headerText"
      :organizationObjectId="enterpriseResponseBody.organizationObjectId"
      @close-enrollment-instructions="handleEnrollmentInstructionClose"
    />
    <Card hasNoBorder>
      <HeadingMedium
        :class="$style.headerBorder"
      >
        {{ addAndroidDevice.copeTitle }}
        <Icon
          icon="error"
        />
      </HeadingMedium>
      <ParagraphBody>
        <span>
          Enroll a company-owned Android device in JumpCloud’s <em :class="$style.textLink">
            Android EMM</em> by clicking <strong>View QR
            code</strong> and following the prompts to enroll the device
          <em :class="$style.textLink">into Android EMM</em>. The enrolled Android device is visible
          in the Admin Portal. Deliver the phone to the user, who can immediately
          access company resources.
        </span>
      </ParagraphBody>
      <template v-slot:footer>
        <ParagraphBody>
          <ButtonSecondary
            :text="addAndroidDevice.copeBody.buttonText"
            @click="handleEnrollmentInstruction"
          />
        </ParagraphBody>
      </template>
    </Card>
  </Container>
</template>

<script>
import { AndroidEmmConsts, ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import {
  ButtonSecondary,
  Card,
  Container,
  HeadingMedium,
  Icon,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import { mapActions } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import GoogleEmmDynamicSelectModal from 'client/MDM/components/GoogleEmmDynamicSelectModal.vue';
import GoogleMdmApi from 'client/api/v2/GoogleMdmApi';
import QrCodeEnrollmentModal from 'client/MDM/components/QrCodeEnrollmentModal.vue';

const googleModule = StoreModuleMap[ItemTypeConsts.mdmGoogle];

export default {
  name: 'CreateAndroidDevice',

  components: {
    ButtonSecondary,
    Card,
    Container,
    GoogleEmmDynamicSelectModal,
    HeadingMedium,
    Icon,
    ParagraphBody,
    QrCodeEnrollmentModal,
  },

  data: () => ({
    addAndroidDevice: AndroidEmmConsts.creatAndroidDeviceText,
    enterpriseResponseBody: null,
    headerText: AndroidEmmConsts.androidEmmQrEnrollmentText.title,
    isShowEnrollmentInstructions: false,
    showDynamicSelectModal: false,
  }),

  mounted() {
    this.handleMounted();
  },

  methods: {
    ...mapActions(googleModule, [
      'updateGoogleEmmEnterpriseObjState',
    ]),
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
    ]),

    handleEnrollmentInstruction() {
      this.isShowEnrollmentInstructions = true;
    },

    handleEnrollmentInstructionClose() {
      this.isShowEnrollmentInstructions = false;
    },

    async handleMounted() {
      try {
        const getEnterpriseRsp = await GoogleMdmApi.getEnterprise();
        if (getEnterpriseRsp.count) {
          const resIndex = getEnterpriseRsp.count - 1;
          this.enterpriseResponseBody = {
            enterpriseObjectId: getEnterpriseRsp.enterprises[resIndex].objectId,
            organizationObjectId: getEnterpriseRsp.enterprises[resIndex].organizationObjectId,
            allowDeviceEnrollment: getEnterpriseRsp.enterprises[resIndex].allowDeviceEnrollment
              ? getEnterpriseRsp.enterprises[resIndex].allowDeviceEnrollment
              : false,
            deviceGroupId: getEnterpriseRsp.enterprises[resIndex].deviceGroupId
              ? getEnterpriseRsp.enterprises[resIndex].deviceGroupId
              : '',
          };
          this.updateGoogleEmmEnterpriseObjState(this.enterpriseResponseBody);
          this.showDynamicSelectModal = true;
        }
      } catch {
        this.triggerErrorNotification({
          title: 'Error',
          message: 'Unable to fetch Google EMM Enterprise.',
        });
      }
    },
  },
};
</script>

<style module>
.headerBorder {
  border-bottom: 1px solid var(--jcGrey200);
  padding-bottom: var(--jc-spacer);
}

.headerBorder span svg {
  margin-left: var(--jc-spacer-small);
  margin-top: var(--jc-spacer-x-small);
  position: absolute;
}

.textLink {
  color: var(--jc-color-primary-fill-default);
  font-style: normal;
}
</style>
